import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Check, Clock, X as XIcon, RefreshCw } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../../lib/adminApi";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const fmt = (n) => Number.isFinite(+n) ? (+n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";
const badge = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};
const iconFor = { completed: Check, pending: Clock, cancelled: XIcon };

export default function TransactionsManager() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: txs }, { data: customers }] = await Promise.all([
        adminApi.get("/admin/transactions"),
        adminApi.get("/admin/customers"),
      ]);
      setItems(txs);
      setUsers(Object.fromEntries(customers.map((c) => [c.id, c])));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => items.reduce((a, t) => { a[t.status] = (a[t.status] || 0) + 1; return a; }, {}), [items]);
  const filtered = filter === "all" ? items : items.filter((t) => t.status === filter);

  const setStatus = async (id, status) => {
    try {
      await adminApi.put(`/admin/transactions/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="transactions-manager-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-display text-lg font-semibold text-[#0B192C]">
          Transactions ({items.length}) <span className="text-sm font-normal text-slate-500">— {counts.pending || 0} pending · {counts.completed || 0} completed</span>
        </h3>
        <div className="flex gap-2">
          {["all", "pending", "completed", "cancelled"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`tx-filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${filter === f ? "bg-[#0B192C] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0B192C]"}`}
            >
              {f}{f !== "all" ? ` (${counts[f] || 0})` : ""}
            </button>
          ))}
          <Button variant="outline" onClick={load} className="rounded-full h-9 px-4 text-sm" data-testid="tx-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm" data-testid="tx-empty-admin">No transactions in this view.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Direction</th>
                  <th className="px-4 py-3">Send</th>
                  <th className="px-4 py-3">Receive</th>
                  <th className="px-4 py-3">Rate</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((t) => {
                  const u = users[t.user_id];
                  const Icon = iconFor[t.status] || Clock;
                  return (
                    <tr key={t.id} data-testid={`admin-tx-row-${t.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-display font-semibold text-[#0B192C]">{u ? `${u.first_name} ${u.last_name}` : "Unknown"}</div>
                        <div className="text-xs text-slate-500">{u?.email || t.user_id.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-[#0B192C]">{t.from_currency} → {t.to_currency}</td>
                      <td className="px-4 py-3 text-[#0B192C]">{t.from_currency === "CAD" ? "$" : "₦"}{fmt(t.from_amount)}</td>
                      <td className="px-4 py-3 text-[#0B192C]">{t.to_currency === "NGN" ? "₦" : "$"}{fmt(t.to_amount)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{t.rate}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold ${badge[t.status] || badge.pending}`}>
                          <Icon size={12} /> {t.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        <Select value={t.status} onValueChange={(v) => setStatus(t.id, v)}>
                          <SelectTrigger className="h-9 w-32 rounded-lg" data-testid={`tx-status-select-${t.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
